import React, { useEffect, useMemo, useState } from "react";
import { Search, Phone, Tag, MessageCircle } from "lucide-react";
import { CATEGORIES } from "../data/categories";
import { api } from "../lib/api";
import { openChatWith } from "../lib/chatBus";

const fmt = (n) => (n == null ? "Sur demande" : `${n} $`);

export default function Catalogue() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/items").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const catOk = active === "all" || it.category === active;
      const q = query.trim().toLowerCase();
      const qOk = !q || it.name.toLowerCase().includes(q) || (it.description || "").toLowerCase().includes(q);
      return catOk && qOk;
    });
  }, [active, query, items]);

  const categoryLabel = (id) => CATEGORIES.find((c) => c.id === id)?.label || id;

  return (
    <main data-testid="catalog-page" className="bg-white">
      {/* HEADER */}
      <section className="bg-[#1A4E72] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-stripes opacity-20" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-20 md:py-28">
          <div className="font-body text-xs font-bold uppercase tracking-[0.3em] text-[#E87B3A]">
            Inventaire complet
          </div>
          <h1 className="font-display text-6xl md:text-7xl uppercase mt-3 leading-[0.9]">
            Catalogue<br/>d’<span className="text-[#E87B3A]">outils &amp; remorques</span>
          </h1>
          <p className="font-body text-white/80 text-lg mt-5 max-w-2xl">
            {items.length} articles disponibles à la location. Tarifs à la journée et à la semaine.
            Réservation par téléphone avec Phil.
          </p>
        </div>
      </section>

      {/* TOOLBAR */}
      <section className="sticky top-20 z-30 bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-5 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un outil…"
              data-testid="catalog-search"
              className="w-full pl-10 pr-3 py-3 border border-[#E2E8F0] rounded-sm font-body text-sm focus:outline-none focus:border-[#E87B3A] focus:ring-1 focus:ring-[#E87B3A]"
            />
          </div>
          <details className="category-dropdown lg:hidden" data-testid="catalog-dropdown">
            <summary><span>{categoryLabel(active)}</span><span className="category-chevron" aria-hidden>⌄</span></summary>
            <div className="category-options">
              {CATEGORIES.map((c) => (
                <button key={c.id} type="button" aria-pressed={active === c.id}
                  onClick={(e) => { setActive(c.id); e.currentTarget.closest('details').removeAttribute('open'); }}>
                  {c.label}<span aria-hidden>{active === c.id ? '✓' : ''}</span>
                </button>
              ))}
            </div>
          </details>
          <div className="hidden lg:flex gap-2 flex-wrap" data-testid="catalog-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                aria-pressed={active === c.id}
                onClick={() => setActive(c.id)}
                data-testid={`filter-${c.id}`}
                className={`px-4 py-2 rounded-sm font-body text-xs font-bold uppercase tracking-wider border transition-all ${
                  active === c.id
                    ? "bg-[#1A4E72] text-white border-[#1A4E72]"
                    : "bg-white text-[#1A4E72] border-[#E2E8F0] hover:border-[#1A4E72]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="font-body text-sm text-[#475569]">
            <span className="font-bold text-[#0F172A]" data-testid="catalog-count">{filtered.length}</span> article{filtered.length > 1 ? "s" : ""} {active !== "all" && (<> · <span className="text-[#3A6D3B] font-bold">{categoryLabel(active)}</span></>)}
          </div>
          <a
            href="tel:8193503027"
            className="hidden md:inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#E87B3A] hover:text-[#D96C2A]"
            data-testid="catalog-header-phone"
          >
            <Phone size={14} /> 819-350-3027
          </a>
        </div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-[#E2E8F0] p-16 text-center rounded-sm">
            <p className="font-body text-[#475569]">Aucun article ne correspond à votre recherche.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((it) => (
              <article
                key={it.id}
                data-testid={`item-${it.id}`}
                className="group bg-white border border-[#E2E8F0] rounded-sm overflow-hidden hover:border-[#1A4E72] hover:-translate-y-1 hover:shadow-md transition-all duration-200 flex flex-col"
              >
                {it.image_url && (
                  <div className="aspect-[4/3] overflow-hidden bg-[#F1F5F9]">
                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#3A6D3B] bg-[#3A6D3B]/10 px-2 py-1 rounded-sm">
                    <Tag size={10} /> {categoryLabel(it.category)}
                  </span>
                  <span className="font-display text-xs text-[#94A3B8]">#{String(it.id).padStart(3, "0")}</span>
                </div>

                <h3 className="font-display text-2xl text-[#1A4E72] uppercase leading-tight">
                  {it.name}
                </h3>
                {it.description && (
                  <p className="font-body text-sm text-[#475569] mt-2 leading-relaxed flex-grow">
                    {it.description}
                  </p>
                )}

                <div className="mt-5 pt-5 border-t border-[#E2E8F0] grid grid-cols-2 gap-3">
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Jour</div>
                    <div className={`font-display text-2xl mt-1 ${it.day ? "text-[#0F172A]" : "text-[#94A3B8]"}`}>
                      {fmt(it.day)}
                    </div>
                  </div>
                  <div>
                    <div className="font-body text-[10px] font-bold uppercase tracking-widest text-[#94A3B8]">Semaine</div>
                    <div className={`font-display text-2xl mt-1 ${it.week ? "text-[#E87B3A]" : "text-[#94A3B8]"}`}>
                      {fmt(it.week)}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    openChatWith(
                      `Bonjour Phil, je souhaite réserver « ${it.name} ».` +
                      (it.day || it.week ? ` (Tarif affiché : ${it.day ? `${it.day} $/jour` : ""}${it.day && it.week ? " · " : ""}${it.week ? `${it.week} $/semaine` : ""})` : "") +
                      `\nDisponibilité pour :`
                    )
                  }
                  data-testid={`item-${it.id}-reserve`}
                  className="mt-5 group inline-flex items-center justify-center gap-2 bg-[#E87B3A] hover:bg-[#F08A47] text-white px-4 py-2.5 rounded-sm font-body font-bold text-xs uppercase tracking-[0.18em] transition-colors"
                >
                  <MessageCircle size={14} /> Réserver
                </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
